import React, { useContext, useState } from "react";
import { X, Plus, Minus, ArrowLeft, CreditCard } from "lucide-react";
import { CartContext } from "../context/CartContext";

const CartDrawer = ({ onClose }) => {
  const context = useContext(CartContext);
  const cartItems = Array.isArray(context.cart) ? context.cart : [];
  const removeFromCart = context.removeFromCart;
  const updateQuantity = context.updateQuantity;

  // Drawer stages: "cart" → "checkout" → "payment"
  const [stage, setStage] = useState("cart");

  const getTotalPrice =
    typeof context.getTotalPrice === "function"
      ? context.getTotalPrice
      : () =>
          cartItems
            .reduce(
              (sum, item) => sum + ((item.price || 0) * (item.quantity || 1)),
              0
            )
            .toFixed(2);

  const total = getTotalPrice();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-end z-50">
      <div className="w-96 bg-white h-full shadow-lg relative flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b bg-gray-900 text-white">
          <div className="flex items-center space-x-2">
            {stage !== "cart" && (
              <button onClick={() => setStage("cart")}>
                <ArrowLeft size={20} />
              </button>
            )}
            <h2 className="text-lg font-bold">
              {stage === "cart"
                ? "Your Cart"
                : stage === "checkout"
                ? "Checkout Details"
                : "Payment"}
            </h2>
          </div>
          <button onClick={onClose}>
            <X size={22} className="text-white hover:text-red-400" />
          </button>
        </div>

        {/* Stage 1: CART ITEMS */}
        {stage === "cart" && (
          <div className="flex-1 overflow-y-auto p-4">
            {cartItems.length === 0 ? (
              <p className="text-gray-500 text-center mt-10">
                Your cart is empty.
              </p>
            ) : (
              cartItems.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between border-b py-3"
                >
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-12 h-12 object-contain"
                  />
                  <div className="flex-1 px-3">
                    <h3 className="font-semibold text-sm">{item.name}</h3>
                    <p className="text-amber-600 font-bold text-sm">
                      ${item.price}
                    </p>
                    <div className="flex items-center mt-1">
                      <button
                        onClick={() =>
                          updateQuantity &&
                          updateQuantity(
                            item.id,
                            Math.max(item.quantity - 1, 1)
                          )
                        }
                        className="px-2 py-1 bg-gray-200 rounded-l hover:bg-gray-300"
                      >
                        <Minus size={14} />
                      </button>
                      <span className="px-3 py-1 bg-gray-100 border-t border-b text-sm">
                        {item.quantity}
                      </span>
                      <button
                        onClick={() =>
                          updateQuantity &&
                          updateQuantity(item.id, item.quantity + 1)
                        }
                        className="px-2 py-1 bg-gray-200 rounded-r hover:bg-gray-300"
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                  </div>
                  <button
                    onClick={() => removeFromCart && removeFromCart(item.id)}
                    className="text-red-500 hover:text-red-700 text-sm"
                  >
                    Remove
                  </button>
                </div>
              ))
            )}
          </div>
        )}

        {/* Stage 2: CHECKOUT DETAILS */}
        {stage === "checkout" && (
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            <h3 className="text-lg font-semibold mb-2 text-gray-800">
              Delivery Address
            </h3>
            <input
              type="text"
              placeholder="Full Name"
              className="w-full p-2 border rounded-md"
            />
            <input
              type="text"
              placeholder="Address Line 1"
              className="w-full p-2 border rounded-md"
            />
            <input
              type="text"
              placeholder="City"
              className="w-full p-2 border rounded-md"
            />
            <input
              type="text"
              placeholder="ZIP / Postal Code"
              className="w-full p-2 border rounded-md"
            />
            <input
              type="text"
              placeholder="Phone Number"
              className="w-full p-2 border rounded-md"
            />
            <div className="pt-4">
              <h4 className="font-semibold">Order Summary</h4>
              <p className="text-gray-600 mt-1">
                Total items: {cartItems.length}
              </p>
              <p className="font-bold text-amber-600">Total: ${total}</p>
            </div>
          </div>
        )}

        {/* Stage 3: PAYMENT */}
        {stage === "payment" && (
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            <h3 className="text-lg font-semibold text-gray-800">
              Payment Method
            </h3>

            <div className="space-y-3">
              <label className="flex items-center space-x-2">
                <input type="radio" name="payment" defaultChecked />
                <span>Credit / Debit Card</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="radio" name="payment" />
                <span>Cash on Delivery (COD)</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="radio" name="payment" />
                <span>UPI / Wallet</span>
              </label>
            </div>

            <div className="mt-6">
              <h4 className="font-semibold">Card Details</h4>
              <input
                type="text"
                placeholder="Card Number"
                className="w-full p-2 border rounded-md mt-2"
              />
              <div className="flex space-x-2 mt-2">
                <input
                  type="text"
                  placeholder="MM/YY"
                  className="w-1/2 p-2 border rounded-md"
                />
                <input
                  type="text"
                  placeholder="CVV"
                  className="w-1/2 p-2 border rounded-md"
                />
              </div>
            </div>
          </div>
        )}

        {/* Footer Actions */}
        {cartItems.length > 0 && (
          <div className="p-4 border-t bg-gray-50">
            {stage === "cart" && (
              <>
                <div className="flex justify-between font-semibold mb-3">
                  <span>Total:</span>
                  <span className="text-amber-600">${total}</span>
                </div>
                <button
                  onClick={() => setStage("checkout")}
                  className="w-full bg-amber-500 text-white py-2 rounded-md hover:bg-amber-600 font-semibold"
                >
                  Proceed to Checkout
                </button>
              </>
            )}

            {stage === "checkout" && (
              <button
                onClick={() => setStage("payment")}
                className="w-full bg-green-600 text-white py-2 rounded-md hover:bg-green-700 font-semibold"
              >
                Continue to Payment
              </button>
            )}

            {stage === "payment" && (
              <button
                onClick={() =>
                  alert(
                    `Payment successful! 🎉\nYour order of $${total} is placed.`
                  )
                }
                className="w-full bg-amber-500 text-white py-2 rounded-md hover:bg-amber-600 font-semibold flex items-center justify-center"
              >
                <CreditCard size={18} className="mr-2" /> Pay ${total}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CartDrawer;
